import { AccessTime, Repeat } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DialogContentText from '@mui/material/DialogContentText';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { format, parseISO } from 'date-fns';
import { Field, Form, Formik, useField } from 'formik';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import { selectEvent } from '@sophocles/features/calendar/slice';
import { addToast } from '@sophocles/features/toast';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';
import { relayLoadLabel } from '@sophocles/views/sites/utils/schedulerEventAdapter';

import RRULEBuilder from '../components/RRULEBuilder';
import {
  buildCalendarEventValidationSchema,
  buildRecurrenceDescriptor,
  parseCalendarEventInitialValues,
  snapToFifteenMinutes,
} from '../forms/calendarEventForm';

// Helper component for date picker
const MyDatePicker = ({ name, label, disabled = false }) => {
  const [field, meta, helpers] = useField(name);

  return (
    <DatePicker
      label={label}
      disabled={disabled}
      slotProps={{
        textField: {
          name,
          size: 'small',
          onBlur: () => helpers.setTouched(true, true),
          error: meta.touched && Boolean(meta.error),
          helperText: meta.touched && meta.error,
        },
      }}
      value={field.value}
      onChange={data => {
        helpers.setTouched(true, false);
        helpers.setValue(data, true);
      }}
    />
  );
};

// Helper component for timing type selector
const TimingTypeSelector = ({ name, label, exclude = [], supportsNegativeOffsets = true, isOnTiming = true }) => {
  const { t } = useTranslation();
  const [field, meta, helpers] = useField(name);

  let timingTypes = [
    { value: 'time', label: t('calendar.eventDialog.timingTypes.time', 'Time') },
    { value: 'sunrise', label: t('calendar.eventDialog.timingTypes.sunrise', 'Sunrise') },
    { value: 'sunset', label: t('calendar.eventDialog.timingTypes.sunset', 'Sunset') },
  ].filter(type => !exclude.includes(type.value));

  // For non-V2 units: ON can only be 'time' or 'sunset', OFF can only be 'time' or 'sunrise'
  if (!supportsNegativeOffsets) {
    if (isOnTiming) {
      // ON timing: only allow 'time' or 'sunset'
      timingTypes = timingTypes.filter(type => type.value === 'time' || type.value === 'sunset');
    } else {
      // OFF timing: only allow 'time' or 'sunrise'
      timingTypes = timingTypes.filter(type => type.value === 'time' || type.value === 'sunrise');
    }
  }

  return (
    <FormControl size="small" sx={{ minWidth: 140 }} error={meta.touched && Boolean(meta.error)}>
      <InputLabel>{label}</InputLabel>
      <Select {...field} label={label} onChange={e => helpers.setValue(e.target.value)}>
        {timingTypes.map(type => (
          <MenuItem key={type.value} value={type.value}>
            {type.label}
          </MenuItem>
        ))}
      </Select>
      {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
    </FormControl>
  );
};

// Helper component for time picker with 15-minute increments
const MyTimePicker = ({ name, label, disabled = false }) => {
  const [field, meta, helpers] = useField(name);

  return (
    <TimePicker
      label={label}
      disabled={disabled}
      minutesStep={15}
      slotProps={{
        textField: {
          name,
          size: 'small',
          sx: { minWidth: 140 },
          // Both call sites pass label="" (the row already reads "On"/"Off" from
          // the adjacent TimingTypeSelector) — hiddenLabel drops the padding
          // filled otherwise reserves for a floating label that never renders.
          hiddenLabel: !label,
          onBlur: () => helpers.setTouched(true, true),
          error: meta.touched && Boolean(meta.error),
          helperText: meta.touched && meta.error,
        },
      }}
      value={field.value}
      onChange={data => {
        helpers.setTouched(true, false);
        const snappedTime = data ? snapToFifteenMinutes(data) : data;
        helpers.setValue(snappedTime, true);
      }}
    />
  );
};

function CalendarEventDialog({
  open,
  relayId,
  relays,
  eventId,
  selectedDate,
  siteTimeZone,
  supportsNegativeOffsets = false,
  disabled = false,
  onSubmit,
  onDelete,
  onClose,
  ...props
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const relay = relays.find(r => r.id === relayId);
  // A calendar event drives loads, so only relays that have loads are selectable.
  const selectableRelays = relays.filter(r => (r.loads?.length ?? 0) > 0);
  const currentEvent = useSelector(selectEvent(eventId));
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  // A relay's display label: its load names (shared with the calendar resource
  // rows so they read identically), else its (paired) relay number.
  const relayOptionLabel = r => {
    if ((r.loads?.length ?? 0) > 0) {
      return relayLoadLabel(r, t);
    }
    const secondaryRelay =
      r.secondary_relay_id != null ? relays.find(other => other.id === r.secondary_relay_id) : null;
    const relayDisplayInfo = secondaryRelay
      ? t('calendar.relayWithPaired', { number: r.relay_number, secondary: secondaryRelay.relay_number })
      : t('calendar.relayLabel', { number: r.relay_number });
    return r.name || relayDisplayInfo;
  };

  // In create mode the relay selector shows the relay, so the title stays plain.
  const getDialogTitle = () => {
    if (!eventId) {
      return t('calendar.actions.createNewEvent', 'Create New Event');
    }
    return relay
      ? `${t('calendar.actions.editEvent', 'Edit Event')} - ${relayOptionLabel(relay)}`
      : t('calendar.actions.editEvent', 'Edit Event');
  };

  // Derived synchronously (not state + effect) so the form's first render on
  // open already carries the preselected relay: a relay Select that first
  // renders with an empty value latches MUI's uncontrolled mode and its label
  // never floats, even once the value arrives. eventId null means create mode,
  // which takes fresh defaults instead of the previously selected event.
  const initialValues = useMemo(
    () => parseCalendarEventInitialValues(eventId ? currentEvent : null, { relayId, selectedDate, siteTimeZone }),
    [eventId, currentEvent, relayId, selectedDate, siteTimeZone],
  );

  const validationSchema = buildCalendarEventValidationSchema(t, supportsNegativeOffsets);

  // Helper function to build event data
  const buildEventData = values => {
    const parsedRecurrence = buildRecurrenceDescriptor(values.recurrence);
    const selectedRelay = relays.find(r => r.id === values.relay_id);
    return {
      id: values.id,
      name: values.name,
      relay: {
        type: 'Relay',
        id: values.relay_id,
        relay_number: selectedRelay?.relay_number || 1,
      },
      enabled: values.enabled,
      always_on: values.always_on,
      start_date: values.start_date ? format(values.start_date, 'yyyy-MM-dd') : null,
      end_date: values.end_date ? format(values.end_date, 'yyyy-MM-dd') : null,
      on_timing: values.always_on
        ? null
        : {
            type: values.on_timing.timing_type,
            time:
              values.on_timing.timing_type === 'time'
                ? (() => {
                    const timeDate = new Date(values.on_timing.time);
                    timeDate.setSeconds(0, 0); // Ensure seconds are 0
                    return format(timeDate, 'HH:mm:ss');
                  })()
                : null,
            offset: values.on_timing.offset || 0,
          },
      off_timing: values.always_on
        ? null
        : {
            type: values.off_timing.timing_type,
            time:
              values.off_timing.timing_type === 'time'
                ? (() => {
                    const timeDate = new Date(values.off_timing.time);
                    timeDate.setSeconds(0, 0); // Ensure seconds are 0
                    return format(timeDate, 'HH:mm:ss');
                  })()
                : null,
            offset: values.off_timing.offset || 0,
          },
      recurrence: parsedRecurrence
        ? {
            parsed: parsedRecurrence,
            exception_dates: values.recurrence.exception_dates || [],
          }
        : null,
    };
  };

  const handleSubmit = (values, { setErrors }) => {
    if (disabled) return Promise.resolve(false);

    // Check if this is a recurring event being changed to non-recurring
    const wasRecurring = Boolean(currentEvent?.recurrence?.parsed);
    const isBecomingNonRecurring = wasRecurring && values.recurrence.repeat_type === 'never';

    // If changing from recurring to non-recurring, we need to show series choice dialog
    if (isBecomingNonRecurring) {
      // Call onSubmit with a special flag to indicate this needs series choice handling
      const eventData = buildEventData(values);
      return onSubmit(eventData, { requiresSeriesChoice: true, formValues: values });
    }

    setNonFieldMessages([]);
    try {
      const eventData = buildEventData(values);

      return onSubmit(eventData)
        .then(saved => {
          if (saved === false) return;
          dispatch(
            addToast({
              message: eventId ? t('calendar.messages.eventUpdated') : t('calendar.messages.eventCreated'),
              level: 'success',
            }),
          );
        })
        .catch(err => {
          console.error('Error submitting event:', err);
          setNonFieldMessages(
            applyApiErrorToFormik(err, {
              setErrors,
              knownFields: [],
              genericMessage: t('calendar.messages.errorSaving'),
            }),
          );
        });
    } catch (error) {
      setNonFieldMessages(
        applyApiErrorToFormik(error, {
          setErrors,
          knownFields: [],
          genericMessage: t('calendar.messages.errorSaving'),
        }),
      );
    }
  };

  return (
    <Formik
      key={`calendar-event-dialog-${eventId || 'new'}-${open}`}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, isValid, submitForm, setFieldValue, resetForm }) => {
        // Helper function to ensure form is always reset when closing
        const handleClose = () => {
          resetForm();
          setNonFieldMessages([]);
          onClose();
        };

        return (
          <Form>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth {...props}>
              <DialogTitle>{getDialogTitle()}</DialogTitle>
              <DialogContent>
                <Field name="name">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('calendar.eventDialog.name', 'Event Name')}
                      placeholder={t('calendar.eventDialog.addTitle', 'Add title')}
                      variant="outlined"
                      margin="normal"
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>

                {!eventId && (
                  <Field name="relay_id">
                    {({ field }) => (
                      <FormControl fullWidth margin="normal" size="small">
                        <InputLabel id="calendar-event-relay-label">
                          {t('calendar.eventDialog.relay', 'Relay')}
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="calendar-event-relay-label"
                          label={t('calendar.eventDialog.relay', 'Relay')}
                        >
                          {selectableRelays.map(r => (
                            <MenuItem key={r.id} value={r.id}>
                              {relayOptionLabel(r)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Field>
                )}

                {/* Form Content */}
                <Box>
                  <Stack spacing={4}>
                    {/* Date and Time Section */}
                    <Box>
                      <Stack direction="row" alignItems="flex-start" spacing={2}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            color: 'text.secondary',
                            flexShrink: 0,
                          }}
                        >
                          <AccessTime />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          {/* Date Row */}
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <Field name="start_date">
                              {({ field, meta }) => (
                                <TextField
                                  type="date"
                                  value={format(field.value, 'yyyy-MM-dd')}
                                  onChange={e => {
                                    const newDate = parseISO(e.target.value);
                                    setFieldValue('start_date', newDate);
                                    // Auto-update end date in these cases:
                                    // 1. Currently a single day event, OR
                                    // 2. Repeat type is never (force single day), OR
                                    // 3. Repeat type is not never but no end date set yet (auto multi-day)
                                    if (
                                      format(values.start_date, 'yyyy-MM-dd') ===
                                        format(values.end_date, 'yyyy-MM-dd') ||
                                      values.recurrence?.repeat_type === 'never' ||
                                      (values.recurrence?.repeat_type !== 'never' &&
                                        values.recurrence?.repeat_type !== undefined &&
                                        format(values.start_date, 'yyyy-MM-dd') ===
                                          format(values.end_date, 'yyyy-MM-dd'))
                                    ) {
                                      if (values.recurrence?.repeat_type === 'never') {
                                        // Single day for non-recurring
                                        setFieldValue('end_date', newDate);
                                      } else if (
                                        values.recurrence?.repeat_type &&
                                        values.recurrence?.repeat_type !== 'never'
                                      ) {
                                        // Multi-day for recurring - add one day
                                        const nextDay = new Date(newDate);
                                        nextDay.setDate(nextDay.getDate() + 1);
                                        setFieldValue('end_date', nextDay);
                                      } else {
                                        // Default case - same date
                                        setFieldValue('end_date', newDate);
                                      }
                                    }
                                  }}
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: 'transparent',
                                      '& fieldset': {
                                        borderColor: 'divider',
                                      },
                                    },
                                    minWidth: 140,
                                  }}
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                />
                              )}
                            </Field>

                            {/* Show end date field if repeat type is not 'never' OR dates are different */}
                            {((values.recurrence?.repeat_type !== 'never' &&
                              values.recurrence?.repeat_type !== undefined) ||
                              format(values.start_date, 'yyyy-MM-dd') !== format(values.end_date, 'yyyy-MM-dd')) && (
                              <>
                                <Typography variant="body2" color="text.secondary">
                                  –
                                </Typography>
                                <Field name="end_date">
                                  {({ field }) => (
                                    <TextField
                                      type="date"
                                      value={format(field.value, 'yyyy-MM-dd')}
                                      onChange={e => {
                                        setFieldValue('end_date', parseISO(e.target.value));
                                      }}
                                      variant="outlined"
                                      size="small"
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          backgroundColor: 'transparent',
                                          '& fieldset': {
                                            borderColor: 'divider',
                                          },
                                        },
                                        minWidth: 140,
                                      }}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                    />
                                  )}
                                </Field>
                              </>
                            )}
                          </Stack>

                          {/* Time Row - only show if not all day */}
                          {!values.always_on && (
                            <Stack spacing={0.5} sx={{ mb: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                {/* On Time Controls */}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <TimingTypeSelector
                                    name="on_timing.timing_type"
                                    label={t('calendar.eventDialog.onType', 'On')}
                                    supportsNegativeOffsets={supportsNegativeOffsets}
                                    isOnTiming={true}
                                  />
                                  {values.on_timing.timing_type === 'time' ? (
                                    <MyTimePicker name="on_timing.time" label="" />
                                  ) : (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      {/* When/At selector */}
                                      <Field name="on_timing.offset">
                                        {({ field }) => {
                                          const absValue = Math.abs(field.value);
                                          const timing = field.value < 0 ? 'before' : field.value > 0 ? 'after' : 'at';

                                          return (
                                            <>
                                              <FormControl size="small" hiddenLabel sx={{ minWidth: 80 }}>
                                                <Select
                                                  value={timing}
                                                  onChange={e => {
                                                    const newTiming = e.target.value;
                                                    if (newTiming === 'at') {
                                                      setFieldValue('on_timing.offset', 0);
                                                    } else if (newTiming === 'before') {
                                                      setFieldValue('on_timing.offset', -(absValue || 15));
                                                    } else {
                                                      setFieldValue('on_timing.offset', absValue || 15);
                                                    }
                                                  }}
                                                >
                                                  {/* For non-V2, ON can only be "before" or "at" (offset <= 0) */}
                                                  <MenuItem value="before">
                                                    {t('calendar.eventDialog.before', 'Before')}
                                                  </MenuItem>
                                                  <MenuItem value="at">{t('calendar.eventDialog.at', 'At')}</MenuItem>
                                                  {supportsNegativeOffsets && (
                                                    <MenuItem value="after">
                                                      {t('calendar.eventDialog.after', 'After')}
                                                    </MenuItem>
                                                  )}
                                                </Select>
                                              </FormControl>
                                              {timing !== 'at' && (
                                                <TextField
                                                  type="number"
                                                  value={absValue}
                                                  onChange={e => {
                                                    const val = parseInt(e.target.value) || 0;
                                                    setFieldValue('on_timing.offset', timing === 'before' ? -val : val);
                                                  }}
                                                  size="small"
                                                  sx={{ width: 70 }}
                                                  slotProps={{
                                                    input: { step: 1, min: 0 },
                                                    inputLabel: { shrink: true },
                                                  }}
                                                  label={t('calendar.eventDialog.minutes', 'min')}
                                                />
                                              )}
                                            </>
                                          );
                                        }}
                                      </Field>
                                    </Stack>
                                  )}
                                </Stack>

                                <Typography variant="body2" color="text.secondary">
                                  –
                                </Typography>

                                {/* Off Time Controls */}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <TimingTypeSelector
                                    name="off_timing.timing_type"
                                    label={t('calendar.eventDialog.offType', 'Off')}
                                    supportsNegativeOffsets={supportsNegativeOffsets}
                                    isOnTiming={false}
                                  />
                                  {values.off_timing.timing_type === 'time' ? (
                                    <MyTimePicker name="off_timing.time" label="" />
                                  ) : (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      {/* When/At selector */}
                                      <Field name="off_timing.offset">
                                        {({ field }) => {
                                          const absValue = Math.abs(field.value);
                                          const timing = field.value < 0 ? 'before' : field.value > 0 ? 'after' : 'at';

                                          return (
                                            <>
                                              <FormControl size="small" hiddenLabel sx={{ minWidth: 80 }}>
                                                <Select
                                                  value={timing}
                                                  onChange={e => {
                                                    const newTiming = e.target.value;
                                                    if (newTiming === 'at') {
                                                      setFieldValue('off_timing.offset', 0);
                                                    } else if (newTiming === 'before') {
                                                      setFieldValue('off_timing.offset', -(absValue || 15));
                                                    } else {
                                                      setFieldValue('off_timing.offset', absValue || 15);
                                                    }
                                                  }}
                                                >
                                                  {/* For non-V2, OFF can only be "at" or "after" (offset >= 0) */}
                                                  {supportsNegativeOffsets && (
                                                    <MenuItem value="before">
                                                      {t('calendar.eventDialog.before', 'Before')}
                                                    </MenuItem>
                                                  )}
                                                  <MenuItem value="at">{t('calendar.eventDialog.at', 'At')}</MenuItem>
                                                  <MenuItem value="after">
                                                    {t('calendar.eventDialog.after', 'After')}
                                                  </MenuItem>
                                                </Select>
                                              </FormControl>
                                              {timing !== 'at' && (
                                                <TextField
                                                  type="number"
                                                  value={absValue}
                                                  onChange={e => {
                                                    const val = parseInt(e.target.value) || 0;
                                                    setFieldValue(
                                                      'off_timing.offset',
                                                      timing === 'before' ? -val : val,
                                                    );
                                                  }}
                                                  size="small"
                                                  sx={{ width: 70 }}
                                                  slotProps={{
                                                    input: { step: 1, min: 0 },
                                                    inputLabel: { shrink: true },
                                                  }}
                                                  label={t('calendar.eventDialog.minutes', 'min')}
                                                />
                                              )}
                                            </>
                                          );
                                        }}
                                      </Field>
                                    </Stack>
                                  )}
                                </Stack>
                              </Stack>

                              {/* Descriptive text for sunrise/sunset timing */}
                              {!values.always_on &&
                                (values.on_timing.timing_type !== 'time' ||
                                  values.off_timing.timing_type !== 'time') && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontStyle: 'italic', pl: 1 }}
                                  >
                                    {(() => {
                                      const parts = [];
                                      // On timing
                                      if (values.on_timing.timing_type !== 'time') {
                                        const typeLabel = t(
                                          `calendar.eventDialog.timingTypes.${values.on_timing.timing_type}`,
                                          values.on_timing.timing_type,
                                        );
                                        const offset = values.on_timing.offset || 0;
                                        const abs = Math.abs(offset);
                                        if (offset === 0) {
                                          parts.push(
                                            t('calendar.eventDialog.turnsOnAt', 'Turns on at {{type}}', {
                                              type: typeLabel,
                                            }),
                                          );
                                        } else if (offset < 0) {
                                          parts.push(
                                            t(
                                              'calendar.eventDialog.turnsOnBefore',
                                              'Turns on {{minutes}} min before {{type}}',
                                              { minutes: abs, type: typeLabel },
                                            ),
                                          );
                                        } else {
                                          parts.push(
                                            t(
                                              'calendar.eventDialog.turnsOnAfter',
                                              'Turns on {{minutes}} min after {{type}}',
                                              { minutes: abs, type: typeLabel },
                                            ),
                                          );
                                        }
                                      }
                                      // Off timing
                                      if (values.off_timing.timing_type !== 'time') {
                                        const typeLabel = t(
                                          `calendar.eventDialog.timingTypes.${values.off_timing.timing_type}`,
                                          values.off_timing.timing_type,
                                        );
                                        const offset = values.off_timing.offset || 0;
                                        const abs = Math.abs(offset);
                                        if (offset === 0) {
                                          parts.push(
                                            t('calendar.eventDialog.turnsOffAt', 'turns off at {{type}}', {
                                              type: typeLabel,
                                            }),
                                          );
                                        } else if (offset < 0) {
                                          parts.push(
                                            t(
                                              'calendar.eventDialog.turnsOffBefore',
                                              'turns off {{minutes}} min before {{type}}',
                                              { minutes: abs, type: typeLabel },
                                            ),
                                          );
                                        } else {
                                          parts.push(
                                            t(
                                              'calendar.eventDialog.turnsOffAfter',
                                              'turns off {{minutes}} min after {{type}}',
                                              { minutes: abs, type: typeLabel },
                                            ),
                                          );
                                        }
                                      }
                                      return parts.join(', ');
                                    })()}
                                  </Typography>
                                )}
                            </Stack>
                          )}

                          {/* Controls Row */}
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={values.always_on}
                                  onChange={e => setFieldValue('always_on', e.target.checked)}
                                  size="small"
                                />
                              }
                              label={t('calendar.eventDialog.allDay', 'All day')}
                              sx={{ ml: 0 }}
                            />

                            {/* Add multi-day toggle - affects both dates and recurrence */}
                            {/* Removed "Make recurring/Make single day" buttons per requirements */}
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Recurrence Settings Section */}
                    <Box>
                      <Stack direction="row" alignItems="flex-start" spacing={2}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            color: 'text.secondary',
                            flexShrink: 0,
                          }}
                        >
                          <Repeat />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <RRULEBuilder
                            values={values}
                            setFieldValue={(field, value) => {
                              // Custom handler to intercept repeat type changes
                              if (field === 'recurrence.repeat_type') {
                                const oldRepeatType = values.recurrence?.repeat_type;
                                const newRepeatType = value;

                                // Set the field value first
                                setFieldValue(field, value);

                                // Handle auto date changes based on repeat type
                                if (newRepeatType === 'never' && oldRepeatType !== 'never') {
                                  // Switching to never - make single day
                                  setFieldValue('end_date', values.start_date);
                                } else if (newRepeatType !== 'never' && oldRepeatType === 'never') {
                                  // Switching from never to recurring - make multi-day if currently single day
                                  if (
                                    format(values.start_date, 'yyyy-MM-dd') === format(values.end_date, 'yyyy-MM-dd')
                                  ) {
                                    const nextDay = new Date(values.start_date);
                                    nextDay.setDate(nextDay.getDate() + 1);
                                    setFieldValue('end_date', nextDay);
                                  }
                                }
                              } else {
                                // For all other fields, use standard setFieldValue
                                setFieldValue(field, value);
                              }
                            }}
                          />
                        </Box>
                      </Stack>
                    </Box>

                    {/* Removed "Enabled" toggle per requirements - events are always enabled */}
                  </Stack>
                </Box>

                <FormErrorAlert messages={nonFieldMessages} sx={{ mt: 2 }} />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>{t('actions.cancel', 'Cancel')}</Button>
                <Button onClick={submitForm} variant="contained" color="primary" disabled={!isValid || disabled}>
                  {eventId
                    ? t('calendar.actions.updateEvent', 'Update Event')
                    : t('calendar.actions.createEvent', 'Create Event')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
              <DialogTitle>{t('calendar.actions.deleteEvent')}</DialogTitle>
              <DialogContent>
                <DialogContentText>{t('calendar.messages.confirmDelete')}</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowDeleteDialog(false)} color="inherit">
                  {t('actions.cancel', 'Cancel')}
                </Button>
                <Button
                  onClick={() => {
                    onDelete(eventId).then(deleted => {
                      if (deleted === false) return;
                      setShowDeleteDialog(false);
                      handleClose();
                    });
                  }}
                  variant="contained"
                  color="error"
                  disabled={disabled}
                >
                  {t('calendar.actions.deleteEvent', 'Delete')}
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
}

export default CalendarEventDialog;
